import {
	CLIENT_DOCUMENT_BLUE,
	CLIENT_DOCUMENT_BORDER,
	CLIENT_DOCUMENT_FONT_FAMILY,
	CLIENT_DOCUMENT_TABLE_HEADER,
	createSafePdfFilename,
	escapeHtml,
} from '@/utils/clientDocumentTheme';
import {
	formatRecordDate,
	formatRecordMonth,
	formatRecordVersion,
	getCompanyRecordTemplate,
	normalizeRecordContent,
} from '@/utils/companyRecords';

// As on the director's Word original.
export const COMPANY_RECORD_FOOTER_TEXT =
	'New Order Clean Ltd | Company No. 13205534 | Office 2, Bennet’s House, 21 Leyton Road, Harpenden, Hertfordshire AL5 2HU';

const cell = (value) => escapeHtml(value).replace(/\r?\n/g, '<br>');

const recordTitle = (record) => record?.title || getCompanyRecordTemplate(record?.templateKey)?.title || 'Company record';

export const deriveCompanyRecordFilename = (record) =>
	createSafePdfFilename(
		`${record?.reference || 'NOC'} ${recordTitle(record).replace(/[’']/g, '')} ${formatRecordVersion(record?.version)}`,
		'company-record',
	);

const reviewYear = (value) => String(value || '').slice(0, 4);

/**
 * Review Completed lists every yearly sign-off up to this version, like the rows of the
 * paper form. `history` holds the earlier versions of the same record.
 */
const reviewRows = (record, history) => {
	const versions = [...history, record]
		.filter((entry) => (Number(entry.version) || 0) <= (Number(record.version) || 0))
		.sort((a, b) => (Number(a.version) || 0) - (Number(b.version) || 0));
	return versions
		.map((entry) => {
			const signOff = normalizeRecordContent(entry.content, entry.templateKey).signOff;
			return `
				<tr data-keep-together>
					<td>${escapeHtml(reviewYear(entry.reviewedOn))}</td>
					<td>${cell(signOff.completedBy)}</td>
					<td>${escapeHtml(formatRecordDate(signOff.date))}</td>
				</tr>`;
		})
		.join('');
};

const checklistSection = (section, entries) => `
	<div class="record-flow-block">
		<h2>${escapeHtml(section.title)}</h2>
		${section.intro ? `<p class="record-section-intro">${cell(section.intro)}</p>` : ''}
		<table class="record-table record-checklist-table">
			<colgroup>
				<col style="width: 15%">
				<col style="width: 32%">
				<col style="width: 11%">
				<col style="width: 8%">
				<col style="width: 8.5%">
				<col style="width: 8.5%">
				<col style="width: 17%">
			</colgroup>
			<thead>
				<tr>
					<th>Item</th>
					<th>What to check</th>
					<th>How often</th>
					<th>Who</th>
					<th>Next due</th>
					<th>Done</th>
					<th>Notes</th>
				</tr>
			</thead>
			<tbody>
				${section.rows
					.map((row) => {
						const entry = entries[row.key] || {};
						return `
							<tr data-keep-together>
								<td class="record-item">${cell(row.item)}</td>
								<td>${cell(row.check)}</td>
								<td>${cell(row.frequency)}</td>
								<td>${cell(row.who)}</td>
								<td class="record-date">${escapeHtml(formatRecordDate(entry.nextDue))}</td>
								<td class="record-date">${escapeHtml(formatRecordDate(entry.done))}</td>
								<td>${cell(entry.notes)}</td>
							</tr>`;
					})
					.join('')}
			</tbody>
		</table>
	</div>`;

/** The record as an A4 landscape document, split into pages like the other NOC documents. */
export const generateCompanyRecordHtml = ({ record, history = [] } = {}) => {
	const template = getCompanyRecordTemplate(record?.templateKey);
	const content = normalizeRecordContent(record?.content, record?.templateKey);
	const title = recordTitle(record);
	const reference = record?.reference || template?.reference || '';
	const version = formatRecordVersion(record?.version);

	return `
		<div
			class="pdf-gen-root pdf-gen-invoice-document record-pdf-root"
			data-client-document-autopaginate="company-record"
			data-pdf-orientation="landscape"
		>
			<div class="record-document record-pagination-source" aria-hidden="true">
				<div class="record-flow-block record-cover-block">
					<header class="record-title">
						<p>NEW ORDER CLEAN LTD</p>
						<h1>${escapeHtml(title)}</h1>
					</header>
					<table class="record-info-table">
						<tbody>
							<tr><th>Document Reference</th><td>${escapeHtml(reference)}</td></tr>
							<tr><th>Version</th><td>${escapeHtml(version)}</td></tr>
							<tr><th>Reviewed</th><td>${escapeHtml(formatRecordMonth(record?.reviewedOn))}</td></tr>
							<tr><th>Next Review</th><td>${escapeHtml(formatRecordMonth(record?.nextReviewOn))}${record?.nextReviewOn ? ' (annual)' : ''}</td></tr>
						</tbody>
					</table>
					<ul class="record-intro">
						${content.intro.map((line) => `<li>${cell(line)}</li>`).join('')}
					</ul>
				</div>

				<div class="record-flow-block">
					<h2>Review Completed</h2>
					<table class="record-table record-review-table">
						<colgroup>
							<col style="width: 22%">
							<col style="width: 48%">
							<col style="width: 30%">
						</colgroup>
						<thead>
							<tr>
								<th>Year</th>
								<th>Completed by</th>
								<th>Date</th>
							</tr>
						</thead>
						<tbody>${reviewRows(record || {}, history)}</tbody>
					</table>
				</div>

				${content.sections.map((section) => checklistSection(section, content.entries)).join('')}
			</div>
			<template data-record-footer-template>
				<span>${escapeHtml(COMPANY_RECORD_FOOTER_TEXT)}</span>
				<span>${escapeHtml(`${reference} – ${title}, ${version}`)}</span>
			</template>
		</div>
		<style>
			.record-pdf-root {
				width: 100%;
				background: #f3f4f6;
				font-family: ${CLIENT_DOCUMENT_FONT_FAMILY};
				color: #262626;
			}

			.record-document {
				position: relative;
				width: 11.69in;
				height: 8.26in;
				margin: 0 auto;
				padding: 0.45in 0.55in 0.8in;
				background: #fff;
				box-sizing: border-box;
				font-size: 10.5px;
				line-height: 1.38;
				overflow: hidden;
				page-break-after: always;
				break-after: page;
			}

			.record-document--last {
				page-break-after: auto;
				break-after: auto;
			}

			.record-pagination-source {
				position: absolute;
				top: 0;
				left: -10000px;
				height: auto;
				overflow: visible;
				visibility: hidden;
				pointer-events: none;
				page-break-after: auto;
				break-after: auto;
			}

			.record-flow-block {
				display: flow-root;
			}

			.record-title p,
			.record-title h1 {
				margin: 0;
				color: ${CLIENT_DOCUMENT_BLUE};
				font-size: 16px;
				font-weight: 700;
				line-height: 1.3;
				letter-spacing: 0;
			}

			.record-title {
				margin-bottom: 12px;
			}

			.record-info-table {
				border-collapse: collapse;
				margin-bottom: 10px;
			}

			.record-info-table th,
			.record-info-table td {
				padding: 2px 16px 2px 0;
				text-align: left;
				vertical-align: top;
				font-size: 11.5px;
			}

			.record-info-table th {
				color: ${CLIENT_DOCUMENT_BLUE};
				font-weight: 700;
			}

			.record-intro {
				margin: 0 0 4px;
				padding: 0;
				list-style: none;
			}

			.record-intro li {
				position: relative;
				margin: 0 0 2px;
				padding-left: 22px;
			}

			.record-intro li::before {
				content: '–';
				position: absolute;
				left: 4px;
			}

			.record-document h2 {
				margin: 14px 0 6px;
				color: ${CLIENT_DOCUMENT_BLUE};
				font-size: 12px;
				font-weight: 700;
				line-height: 1.3;
			}

			.record-section-intro {
				margin: 0 0 6px;
			}

			.record-table {
				width: 100%;
				border-collapse: collapse;
				table-layout: fixed;
			}

			.record-review-table {
				width: 52%;
			}

			.record-table th,
			.record-table td {
				padding: 4px 6px;
				border: 1px solid ${CLIENT_DOCUMENT_BORDER};
				text-align: left;
				vertical-align: top;
				overflow-wrap: anywhere;
			}

			.record-table th {
				background: ${CLIENT_DOCUMENT_TABLE_HEADER};
				font-weight: 700;
			}

			.record-review-table td {
				height: 14px;
			}

			.record-table .record-item {
				background: #fafafa;
				font-weight: 600;
			}

			.record-table .record-date {
				white-space: nowrap;
				overflow-wrap: normal;
			}

			.record-page-footer {
				position: absolute;
				left: 0.55in;
				right: 0.55in;
				bottom: 0.32in;
				display: flex;
				align-items: flex-end;
				justify-content: space-between;
				gap: 24px;
				padding-top: 8px;
				border-top: 1px solid #e5e5e5;
				font-size: 8.5px;
				line-height: 1.4;
				color: #555;
			}

			.record-page-footer-text span {
				display: block;
			}

			.record-page-number {
				flex: 0 0 auto;
				color: #777;
			}

			@media print {
				.record-pdf-root {
					background: #fff;
				}

				.record-document {
					margin: 0;
				}
			}
		</style>
	`;
};
